<template>
  <section class="rounded-[24px] border border-[#eee4da] bg-white p-4 shadow-sm sm:p-6">
    <div class="flex flex-col gap-3 border-b border-[#eee4da] pb-5 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <RouterLink to="/dashboard/settings" class="inline-flex items-center gap-2 text-sm font-semibold text-brand">
          <i class="ri-arrow-left-line"></i>
          Settings
        </RouterLink>
        <h2 class="mt-3 text-[1.65rem] font-semibold leading-tight text-ink">Profile</h2>
        <p class="mt-1 text-sm text-muted">Update your vendor account contact details.</p>
      </div>
      <span class="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fbf1e6] text-2xl text-brand">
        <i class="ri-user-settings-line"></i>
      </span>
    </div>

    <form class="mt-6 grid gap-4 md:grid-cols-2" @submit.prevent="saveProfile">
      <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
        Vendor name <span class="text-brand">*</span>
        <input v-model.trim="form.name" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal text-ink focus:border-brand focus:outline-none">
        <p v-if="errors.name" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ errors.name }}</p>
      </label>

      <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
        Email <span class="text-brand">*</span>
        <input v-model.trim="form.email" type="email" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal text-ink focus:border-brand focus:outline-none">
        <p v-if="errors.email" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ errors.email }}</p>
      </label>

      <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
        Phone
        <input v-model.trim="form.phone" type="tel" inputmode="numeric" maxlength="10" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal text-ink focus:border-brand focus:outline-none" @input="form.phone = form.phone.replace(/\\D/g, '').slice(0, 10)">
      </label>

      <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
        Business / brand name
        <input v-model.trim="form.businessName" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal text-ink focus:border-brand focus:outline-none">
      </label>

      <label class="md:col-span-2 text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
        About
        <textarea v-model.trim="form.about" rows="4" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal text-ink focus:border-brand focus:outline-none"></textarea>
      </label>

      <p v-if="loadError" class="md:col-span-2 text-sm font-semibold text-red-600">{{ loadError }}</p>

      <div class="md:col-span-2 flex flex-col gap-3 sm:flex-row sm:justify-end">
        <RouterLink to="/dashboard/settings" class="inline-flex items-center justify-center rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand">
          Cancel
        </RouterLink>
        <button type="submit" class="inline-flex items-center justify-center rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:opacity-60" :disabled="saving">
          {{ saving ? 'Saving...' : 'Save profile' }}
        </button>
      </div>
    </form>
  </section>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { RouterLink } from 'vue-router'
import Swal from 'sweetalert2'
import api from '@/services/vendor/api'
import { getAuthUser, getAuthUserId, setAuthUser } from '@/services/auth'

const saving = ref(false)
const loadError = ref('')

const form = reactive({
  name: '',
  email: '',
  phone: '',
  businessName: '',
  about: ''
})

const errors = reactive({
  name: '',
  email: ''
})

const normalizeProfile = (data) => data?.user || data?.data?.user || data?.data || data?.profile || data || {}

const hydrate = (profile = {}) => {
  form.name = profile.vendor_name || profile.s_name || profile.name || profile.username || ''
  form.email = profile.email || profile.s_email || ''
  form.phone = String(profile.phone || profile.s_phone || profile.mobile || '').replace(/\D/g, '').slice(0, 10)
  form.businessName = profile.business_name || profile.brand_name || profile.company_name || ''
  form.about = profile.about || profile.description || ''
}

const loadProfile = async () => {
  loadError.value = ''
  hydrate(getAuthUser() || {})
  const vendorId = getAuthUserId()
  if (!vendorId) return
  try {
    const fd = new FormData()
    fd.append('vendor_id', vendorId)
    const { data } = await api.getProfileVendor(fd)
    const profile = normalizeProfile(data)
    hydrate(profile)
    setAuthUser(profile)
  } catch {
    loadError.value = 'Unable to refresh profile right now.'
  }
}

const validate = () => {
  errors.name = form.name ? '' : 'Vendor name is required'
  errors.email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) ? '' : 'Valid email is required'
  return !errors.name && !errors.email
}

const saveProfile = async () => {
  loadError.value = ''
  if (!validate()) return
  const vendorId = getAuthUserId()
  if (!vendorId) {
    loadError.value = 'Vendor id is missing. Sign in again.'
    return
  }

  saving.value = true
  try {
    const fd = new FormData()
    fd.append('id', vendorId)
    fd.append('vendor_id', vendorId)
    fd.append('s_id', vendorId)
    fd.append('vendor_name', form.name)
    fd.append('s_name', form.name)
    fd.append('name', form.name)
    fd.append('email', form.email)
    fd.append('phone', form.phone)
    fd.append('business_name', form.businessName)
    fd.append('description', form.about)
    fd.append('type', 'contact')
    const { data } = await api.updateProfileVendor(fd)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) throw new Error(data?.msg || data?.message || 'Failed to update profile')
    setAuthUser({
      vendor_name: form.name,
      s_name: form.name,
      name: form.name,
      email: form.email,
      phone: form.phone,
      business_name: form.businessName,
      about: form.about
    })
    await Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Profile updated', showConfirmButton: false, timer: 1800 })
  } catch (error) {
    loadError.value = error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Failed to update profile'
  } finally {
    saving.value = false
  }
}

onMounted(loadProfile)
</script>
